import React, { useState, useEffect, useRef } from "react";
import { getDatabase, ref, onValue, push, update, orderByChild, query, get } from "../../Config/matuRtdb";
import { getAuth } from "../../Config/matuAuth";
import {
  MessageCircle,
  Send,
  Search,
  Filter,
  MoreVertical,
  Clock,
  User,
  AlertCircle,
  CheckCircle,
  XCircle,
  Star,
  Phone,
  Mail,
  Calendar,
  Eye,
  EyeOff,
  Archive,
  Trash2,
  RefreshCw,
  Bell,
  BellOff,
  ChevronLeft,
  Settings,
  Mic,
  Paperclip,
  Smile
} from "lucide-react";

export default function ChatManager() {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [chatStats, setChatStats] = useState({
    total: 0,
    active: 0,
    resolved: 0,
    pending: 0,
    today: 0
  });
  const [userDetails, setUserDetails] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const auth = getAuth();
  const db = getDatabase();

  useEffect(() => {
    loadChats();
    loadChatStats();
  }, []);

  useEffect(() => {
    if (selectedChat) {
      loadMessages(selectedChat.userId);
      loadUserDetails(selectedChat.userId);
    }
  }, [selectedChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadChats = () => {
    const chatsRef = ref(db, "chats");
    onValue(chatsRef, (snapshot) => {
      const chatsData = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const userId = childSnapshot.key;
          const chatData = childSnapshot.val();

          // Obtener el último mensaje
          const messages = Object.values(chatData || {});
          const lastMessage = messages[messages.length - 1];

          chatsData.push({
            userId,
            lastMessage: lastMessage?.text || "Sin mensajes",
            lastMessageTime: lastMessage?.timestamp || 0,
            messageCount: messages.length,
            isActive: true,
            userName: lastMessage?.userName || "Usuario",
            unreadCount: messages.filter(m => !m.read && m.sender !== auth.currentUser?.uid).length
          });
        });

        // Ordenar por último mensaje
        chatsData.sort((a, b) => b.lastMessageTime - a.lastMessageTime);
        setChats(chatsData);
      }
      setIsLoading(false);
    });
  };

  const loadChatStats = () => {
    const chatsRef = ref(db, "chats");
    onValue(chatsRef, (snapshot) => {
      if (snapshot.exists()) {
        const today = new Date().setHours(0, 0, 0, 0);
        let total = 0;
        let todayCount = 0;
        let activeCount = 0;
        let resolvedCount = 0;
        let pendingCount = 0;

        snapshot.forEach((childSnapshot) => {
          total++;
          const chatData = childSnapshot.val();
          const messages = Object.values(chatData || {});
          const lastMessage = messages[messages.length - 1];

          if (lastMessage?.timestamp >= today) {
            todayCount++;
          }

          // Determinar estado basado en el último mensaje
          if (lastMessage?.sender === auth.currentUser?.uid) {
            pendingCount++;
          } else {
            activeCount++;
          }
        });

        setChatStats({
          total,
          active: activeCount,
          resolved: resolvedCount,
          pending: pendingCount,
          today: todayCount
        });
      }
    });
  };

  const loadMessages = (userId) => {
    const messagesRef = ref(db, `chats/${userId}`);
    onValue(messagesRef, (snapshot) => {
      const messagesData = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          messagesData.push({
            id: childSnapshot.key,
            ...childSnapshot.val()
          });
        });
        messagesData.sort((a, b) => a.timestamp - b.timestamp);
        setMessages(messagesData);

        // Marcar mensajes como leídos
        markMessagesAsRead(userId, messagesData);
      }
    });
  };

  const loadUserDetails = async (userId) => {
    try {
      const userRef = ref(db, `usuarios/${userId}`);
      const snapshot = await get(userRef);
      if (snapshot.exists()) {
        setUserDetails(snapshot.val());
      }
    } catch (error) {
      console.error("Error loading user details:", error);
    }
  };

  const markMessagesAsRead = (userId, messages) => {
    const unreadMessages = messages.filter(
      m => !m.read && m.sender !== auth.currentUser?.uid
    );

    unreadMessages.forEach(async (message) => {
      const messageRef = ref(db, `chats/${userId}/${message.id}`);
      await update(messageRef, { read: true });
    });
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedChat) return;

    try {
      const messageData = {
        text: newMessage.trim(),
        sender: auth.currentUser.uid,
        timestamp: Date.now(),
        userName: "Admin",
        read: false
      };

      const chatRef = ref(db, `chats/${selectedChat.userId}`);
      await push(chatRef, messageData);
      setNewMessage("");
      inputRef.current?.focus();
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const filteredChats = chats.filter(chat => {
    const matchesSearch = chat.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         chat.lastMessage.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "all" ||
                         (filterStatus === "active" && chat.unreadCount > 0) ||
                         (filterStatus === "pending" && chat.unreadCount === 0);

    return matchesSearch && matchesFilter;
  });

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return "ahora";
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;

    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusColor = (chat) => {
    if (chat.unreadCount > 0) return "bg-green-500";
    if (chat.lastMessageTime > Date.now() - 24 * 60 * 60 * 1000) return "bg-yellow-500";
    return "bg-gray-400";
  };

  const getStatusText = (chat) => {
    if (chat.unreadCount > 0) return "En línea";
    if (chat.lastMessageTime > Date.now() - 24 * 60 * 60 * 1000) return "Reciente";
    return "Inactivo";
  };

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Sidebar con lista de chats */}
      <div className="w-1/3 bg-slate-800 border-r border-slate-700 flex flex-col shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle size={20} />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Soporte al Cliente</h2>
                <p className="text-sm text-white/80">{chatStats.total} conversaciones</p>
              </div>
            </div>
            <button
              onClick={loadChats}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <RefreshCw size={16} />
            </button>
          </div>

          {/* Estadísticas rápidas */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white/20 p-2 rounded-lg text-center">
              <div className="text-lg font-bold">{chatStats.total}</div>
              <div className="text-xs">Total</div>
            </div>
            <div className="bg-white/20 p-2 rounded-lg text-center">
              <div className="text-lg font-bold text-yellow-200">{chatStats.active}</div>
              <div className="text-xs">Activos</div>
            </div>
            <div className="bg-white/20 p-2 rounded-lg text-center">
              <div className="text-lg font-bold text-green-200">{chatStats.today}</div>
              <div className="text-xs">Hoy</div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="p-4 bg-slate-700 border-b border-slate-600">
          <div className="flex gap-2 mb-3">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar chats..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-600 border border-slate-500 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-white placeholder-slate-400"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus("all")}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filterStatus === "all" 
                  ? "bg-blue-500 text-white" 
                  : "bg-slate-600 text-slate-300 hover:bg-slate-500"
              }`}
            >
              Todos ({chats.length})
            </button>
            <button
              onClick={() => setFilterStatus("active")}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filterStatus === "active" 
                  ? "bg-green-500 text-white" 
                  : "bg-slate-600 text-slate-300 hover:bg-slate-500"
              }`}
            >
              Activos ({chats.filter(c => c.unreadCount > 0).length})
            </button>
          </div>
        </div>

        {/* Lista de chats */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-slate-400">
              <MessageCircle size={48} className="mb-2 text-slate-600" />
              <p>No hay chats disponibles</p>
            </div>
          ) : (
            filteredChats.map((chat) => (
              <div
                key={chat.userId}
                onClick={() => setSelectedChat(chat)}
                className={`p-4 border-b border-slate-700 cursor-pointer hover:bg-slate-700 transition-all duration-200 ${
                  selectedChat?.userId === chat.userId ? "bg-slate-700 border-l-4 border-l-blue-500" : ""
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="relative">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {chat.userName.charAt(0).toUpperCase()}
                    </div>
                    <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-slate-800 ${getStatusColor(chat)}`}></div>
                  </div>

                  {/* Contenido */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-white truncate">
                        {chat.userName}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">
                          {formatTime(chat.lastMessageTime)}
                        </span>
                        {chat.unreadCount > 0 && (
                          <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full min-w-[20px] text-center">
                            {chat.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-sm text-slate-300 truncate mb-1">
                      {chat.lastMessage}
                    </p>
                    
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        chat.unreadCount > 0 
                          ? "bg-green-900/50 text-green-300" 
                          : "bg-slate-600 text-slate-400"
                      }`}>
                        {getStatusText(chat)}
                      </span>
                      <span className="text-xs text-slate-500">
                        {chat.messageCount} mensajes
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Área de chat */}
      <div className="flex-1 flex flex-col bg-slate-900">
        {selectedChat ? (
          <>
            {/* Header del chat */}
            <div className="bg-slate-800 border-b border-slate-700 p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedChat(null)}
                    className="lg:hidden p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-300"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  
                  <div className="relative">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {selectedChat.userName.charAt(0).toUpperCase()}
                    </div>
                    <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-slate-800 ${getStatusColor(selectedChat)}`}></div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-white">{selectedChat.userName}</h3>
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <User size={14} />
                        {userDetails.name || "Usuario"}
                      </span>
                      {userDetails.email && (
                        <span className="flex items-center gap-1">
                          <Mail size={14} />
                          {userDetails.email}
                        </span>
                      )}
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        selectedChat.unreadCount > 0 
                          ? "bg-green-900/50 text-green-300" 
                          : "bg-slate-600 text-slate-400"
                      }`}>
                        {getStatusText(selectedChat)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-300">
                    <Phone size={16} />
                  </button>
                  <button className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-300">
                    <MoreVertical size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Mensajes */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-slate-900 to-slate-800">
              {messages.map((message) => {
                const isAdmin = message.sender === auth.currentUser?.uid;
                return (
                  <div
                    key={message.id}
                    className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[70%] px-4 py-3 rounded-2xl shadow-lg ${
                        isAdmin
                          ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                          : "bg-slate-700 text-slate-200 border border-slate-600"
                      }`}
                    >
                      <p className="text-sm leading-relaxed">{message.text}</p>
                      <div className={`flex items-center justify-end mt-2 text-xs ${
                        isAdmin ? "text-white/80" : "text-slate-400"
                      }`}>
                        <span>{formatTime(message.timestamp)}</span>
                        {isAdmin && (
                          <span className="ml-2">
                            {message.read ? "✓✓" : "✓"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input de mensaje - FIJADO */}
            <div className="bg-slate-800 border-t border-slate-700 p-4 shadow-xl">
              <div className="flex items-end gap-3">
                <button className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-300">
                  <Paperclip size={20} />
                </button>
                
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Escribe tu mensaje..."
                    rows={1}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none max-h-32 text-white placeholder-slate-400"
                    style={{ minHeight: '44px' }}
                  />
                  <button className="absolute right-2 bottom-2 p-1 hover:bg-slate-600 rounded-lg transition-colors text-slate-400">
                    <Smile size={20} />
                  </button>
                </div>
                
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim()}
                  className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-slate-400">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <MessageCircle size={48} className="text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-2 text-white">Selecciona un chat</h3>
              <p className="text-slate-400">Elige una conversación de la lista para comenzar a responder</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 